import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./styles/Partnerdetail.css";
import "./styles/Nutanix.css";
import nutanixLogo from "../../assets/images/partners/Nutanix.png";

function useCounter(target, duration = 2000, inView) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const step = Math.ceil(target / (duration / 16));
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setCount(target); clearInterval(timer); }
      else setCount(start);
    }, 16);
    return () => clearInterval(timer);
  }, [target, duration, inView]);
  return count;
}
function useInView(threshold = 0.3) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setInView(true); obs.disconnect(); } }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

const cloudLayers = [
  { cls: "public", label: "PUBLIC CLOUD", title: "Nutanix Cloud Clusters (NC2)", nodes: [{ t: "AWS EC2", c: "purple" }, { t: "Azure VMware", c: "indigo" }, { t: "Google Cloud", c: "indigo" }] },
  { cls: "hybrid", label: "HYBRID LAYER", title: "Prism Central — Unified Management", nodes: [{ t: "Lifecycle Manager", c: "indigo" }, { t: "Flow Network Security", c: "indigo" }, { t: "DR Orchestration", c: "gray" }] },
  { cls: "private", label: "PRIVATE CLOUD", title: "Nutanix AHV + AOS (HCI Core)", nodes: [{ t: "AHV Hypervisor", c: "gray" }, { t: "AOS Storage", c: "gray" }, { t: "Volumes Block", c: "gray" }, { t: "Files NFS/SMB", c: "gray" }] },
  { cls: "edge", label: "EDGE / ROBO", title: "Nutanix Xi IoT / Clusters at Edge", nodes: [{ t: "2-Node Edge", c: "green" }, { t: "Xi IoT", c: "green" }, { t: "Xi Leap DR", c: "green" }] },
];

const hciPillars = [
  { icon: "🖥️", title: "Compute", desc: "x86 servers from any OEM — Dell, HPE, Lenovo — running Nutanix AHV hypervisor or VMware ESXi on converged nodes." },
  { icon: "💾", title: "Storage", desc: "Distributed NVMe-first storage fabric across all nodes — with inline deduplication, compression, and erasure coding." },
  { icon: "🌐", title: "Networking", desc: "Nutanix Flow microsegmentation and virtual networking — zero-trust network security built into the hypervisor." },
  { icon: "🔧", title: "Management", desc: "Prism Central unified control plane managing all clusters globally — one UI, one API, all environments." },
];

const servicesData = [
  { num: "1", icon: "☁️", title: "Nutanix Cloud Infrastructure (NCI)", desc: "The core HCI platform — AOS distributed storage, AHV hypervisor, and Prism management. Replaces legacy three-tier with a single scalable, resilient platform.", tags: ["AOS", "AHV", "Prism Pro"], cls: "nx-service-card--1" },
  { num: "2", icon: "🏗️", title: "Nutanix Cloud Platform (NCP)", desc: "Self-service private cloud with policy-based automation, role-based access, and cost chargeback — delivering IaaS from your own infrastructure.", tags: ["Self-Service IaaS", "Calm DSL", "Cost Governance"], cls: "nx-service-card--2" },
  { num: "3", icon: "🔗", title: "Nutanix Cloud Clusters (NC2)", desc: "Extend Nutanix on-premises to AWS, Azure, or GCP — running the same AHV hypervisor and AOS storage in public cloud for true cloud bursting and migration.", tags: ["NC2 on AWS", "NC2 on Azure", "Hybrid Burst"], cls: "nx-service-card--3" },
  { num: "4", icon: "🐳", title: "Nutanix Kubernetes Engine (NKE)", desc: "Enterprise Kubernetes platform with lifecycle management, GPU scheduling for AI workloads, and integrated storage provisioning — managed by Nutanix Prism.", tags: ["NKE Clusters", "GPU Scheduling", "Helm Registry"], cls: "nx-service-card--4" },
  { num: "5", icon: "📊", title: "Nutanix Unified Storage (NUS)", desc: "Volumes block storage, Files NAS (SMB/NFS), Objects S3-compatible storage, and Mine backup all running on the Nutanix distributed storage fabric.", tags: ["Files NAS", "Volumes Block", "Objects S3"], cls: "nx-service-card--5" },
  { num: "6", icon: "🔒", title: "Nutanix Security Central", desc: "Security posture management and Flow microsegmentation with application-centric network policies — zero-trust security built into the hypervisor fabric.", tags: ["Flow Microseg", "Security Posture", "Zero Trust"], cls: "nx-service-card--6" },
];

const k8sItems = [
  { icon: "🚀", title: "NKE One-Click Kubernetes", desc: "Provision production-ready Kubernetes clusters in minutes with full lifecycle management — upgrades, scaling, and node pool management through Prism Central." },
  { icon: "🤖", title: "GPU-Accelerated AI Pods", desc: "Schedule NVIDIA GPU resources across Kubernetes pods for AI/ML training workloads — with GPU device passthrough and vGPU partitioning support." },
  { icon: "💾", title: "Persistent Storage with Volumes", desc: "Dynamic provisioning of Nutanix Volumes block storage for Kubernetes persistent volume claims — eliminating storage complexity for stateful containerized applications." },
  { icon: "🌐", title: "Service Mesh Integration", desc: "Istio and Calico network policy integration for Kubernetes service mesh security — with Nutanix Flow providing underlying network segmentation." },
];

const aiFeatures = [
  { icon: "🧠", name: "GPT-in-a-Box", desc: "Nutanix's validated AI reference architecture for hosting open-source LLMs (Llama, Mistral, Falcon) on-premises — with NVIDIA GPU nodes managed through Prism." },
  { icon: "⚡", name: "NVIDIA AI Enterprise on Nutanix", desc: "Validated Nutanix infrastructure for NVIDIA AI Enterprise workloads — supporting RAPIDS, TensorRT, NeMo, and Triton Inference Server on HCI." },
  { icon: "📊", name: "Nutanix ML Model Serving", desc: "Kubernetes-native model serving infrastructure using NKE — with GPU auto-scaling, model versioning, and Prometheus monitoring built in." },
  { icon: "🔗", name: "AI Data Pipeline Integration", desc: "Nutanix Objects S3-compatible storage as data lake for AI training datasets — with tiering to public cloud object storage for long-term retention." },
];

const migrationPhases = [
  { icon: "🔍", title: "Discovery", desc: "VMware vCenter or physical server inventory, workload profiling, and Nutanix Move readiness assessment." },
  { icon: "📐", title: "Architecture", desc: "Cluster sizing, network design, storage policy definition, and Prism Central topology planning." },
  { icon: "🔄", title: "Pilot Migration", desc: "Migrate 2–5 non-critical workloads with Nutanix Move — validating network, storage, and performance." },
  { icon: "🚀", title: "Bulk Migration", desc: "Phased migration of production workloads with validated runbooks, cut-over planning, and rollback procedures." },
  { icon: "✅", title: "Validation", desc: "Performance baselining, DR testing, and Prism Pro policy activation across migrated workloads." },
];

const industries = [
  { icon: "🏥", name: "Healthcare", desc: "HIPAA-compliant private cloud for EHR/PACS workloads, VDI for clinical endpoints, and Nutanix Calm for automated policy-driven VM provisioning." },
  { icon: "🏦", name: "Financial Services", desc: "High-availability Nutanix clusters for trading and banking applications, Flow microsegmentation for PCI-DSS workload isolation, and Xi Leap for DR." },
  { icon: "🏛️", name: "Government", desc: "Nutanix on-premises clusters for air-gapped and classified environments, with TAA-compliant hardware and DoD IL4/IL5 architecture reference designs." },
  { icon: "🏭", name: "Manufacturing", desc: "Edge Nutanix clusters for factory floor computing, SCADA system VMs, and OT/IT convergence with network microsegmentation between operational zones." },
  { icon: "🎓", name: "Education", desc: "Nutanix VDI for computer labs and remote student access, self-service provisioning for faculty research VMs, and NC2 for cloud exam capacity." },
  { icon: "🛒", name: "Retail", desc: "Nutanix at corporate headquarters, regional DCs, and large store locations — centrally managed from Prism Central with consistent policy enforcement." },
  { icon: "☁️", name: "Cloud Native SaaS", desc: "Nutanix NKE and NCP for internal platform engineering teams building IaaS and container platforms for product development and CI/CD pipelines." },
  { icon: "⚡", name: "Energy & Utilities", desc: "Nutanix in operational technology environments for SCADA and DCS virtualization, with air-gapped clusters for critical infrastructure compliance." },
];

const certs = [
  { icon: "🏆", name: "Nutanix Partner", desc: "Authorized Nutanix implementation and managed services partner" },
  { icon: "🖥️", name: "NCI Certified", desc: "Nutanix Cloud Infrastructure deployment and administration certified" },
  { icon: "☁️", name: "NCP Certified", desc: "Nutanix Cloud Platform and Calm automation certified specialists" },
  { icon: "🐳", name: "NKE Certified", desc: "Nutanix Kubernetes Engine deployment and management certified" },
];

const faqData = [
  { q: "What is Nutanix HCI and how does it differ from traditional three-tier infrastructure?", a: "Traditional three-tier infrastructure separates compute (blade servers), storage (SAN/NAS), and networking into distinct hardware silos managed by different teams with different tools. This architecture is expensive, complex, and difficult to scale — adding storage requires a SAN expansion project that can take weeks. Nutanix Hyperconverged Infrastructure (HCI) runs compute, storage, and networking on standard x86 servers in a cluster — where each node contributes CPU, RAM, and NVMe/SSD storage to a distributed pool managed by software. Adding capacity means adding nodes — a 30-minute operation with no disruption. Nutanix AOS distributes data across all nodes with replication for redundancy, and Prism Central provides a single management interface for everything. ALG Data Guard migrates organizations from VMware and physical three-tier environments to Nutanix with Nutanix Move — a free migration tool that live-migrates VMs with no downtime." },
  { q: "What is the difference between Nutanix AHV and VMware vSphere for the hypervisor?", a: "Nutanix AHV is Nutanix's included, free KVM-based hypervisor — included in Nutanix licensing at no additional cost, fully supported, and deeply integrated with Prism management. VMware ESXi is VMware's enterprise hypervisor, which runs on Nutanix but requires separate VMware licensing (vSphere Plus, vSphere Foundation, or vSphere Enterprise Plus). With Broadcom's 2024 VMware licensing changes significantly increasing costs, many organizations are evaluating migration from ESXi to AHV to reduce total cost. Nutanix Move migrates VMs from ESXi to AHV — including Windows and Linux VMs, with automatic driver injection and live migration without extended maintenance windows. ALG Data Guard assesses your VMware environment for AHV compatibility and designs a phased migration strategy that minimizes risk." },
  { q: "What is Nutanix Cloud Clusters (NC2) and when should organizations use it?", a: "Nutanix Cloud Clusters (NC2) runs Nutanix AHV and AOS on bare-metal instances in AWS (i3.metal) or Azure (Azure Bare Metal) — giving organizations the identical Nutanix experience in public cloud managed through the same Prism Central as on-premises. NC2 is ideal for three scenarios: cloud bursting capacity during peak demand without rebuilding workloads for native cloud services; data center exit where workloads migrate to cloud temporarily while rearchitecting; and disaster recovery where production runs on-premises but NC2 provides a standby cloud target managed identically. The key advantage is operational consistency — the same Prism policies, security groups, and Calm blueprints work identically on-premises and in NC2." },
  { q: "How does Nutanix handle backup and disaster recovery?", a: "Nutanix provides multiple data protection layers. Nutanix Xi Leap (or Leap on Nutanix) provides VM-level disaster recovery replication between Nutanix clusters — either on-premises to on-premises, on-premises to NC2 in cloud, or between cloud regions — with RPO from 1 hour to 24 hours and RTO in minutes through automated runbook failover. Nutanix Mine with Commvault, Veeam, or HYCU provides backup-as-a-service running on Nutanix hardware — eliminating a separate backup infrastructure. For snapshots, Nutanix AOS provides VM-consistent crash-consistent snapshots with configurable retention schedules managed by Prism. ALG Data Guard designs data protection architectures leveraging all three layers appropriate to your RTO/RPO requirements and budget." },
  { q: "How does ALG Data Guard support Nutanix environments after deployment?", a: "ALG Data Guard provides comprehensive Nutanix managed services: Prism Central health monitoring and alert response, Nutanix Lifecycle Manager (LCM) coordination for firmware and software updates, cluster expansion planning and node additions, Nutanix Move VM migrations for ongoing infrastructure refresh, NKE Kubernetes cluster management, Nutanix Files NAS administration, performance capacity planning using Prism Pro analytics, and quarterly Nutanix health assessment reports. For organizations migrating from VMware, we also provide ongoing AHV support and vSphere migration project management as you transition workloads over time." },
];

const ArrowIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12h14M13 5l7 7-7 7"/></svg>
);
const ChevronDown = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6"/></svg>
);

export default function Nutanix() {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);
  const [metricsRef, metricsInView] = useInView(0.3);
  const c1 = useCounter(25000, 2500, metricsInView);
  const c2 = useCounter(60, 2000, metricsInView);
  const c3 = useCounter(99, 2000, metricsInView);
  const c4 = useCounter(50, 2000, metricsInView);

  return (
    <div className="nx-page pd-page">

      {/* ── Identity Band ── */}
      <div className="pd-identity-band">
        <div className="pd-identity-inner">
          <img src={nutanixLogo} alt="Nutanix" className="pd-partner-logo" />
          <div className="pd-identity-divider" aria-hidden="true" />
          <div className="pd-identity-text">
            <div className="pd-eyebrow pd-eyebrow--dark">
              <span className="pd-live-dot pd-live-dot--dark" />
              Hybrid Multicloud Infrastructure Partner
            </div>
            <h1 className="pd-identity-name">ALG Data Guard × Nutanix</h1>
            <p className="pd-identity-desc">
              Nutanix is the world's leading hyperconverged infrastructure and hybrid multicloud platform — delivering unified compute, storage, networking, and cloud management through its enterprise software stack. Organizations across every industry use Nutanix to simplify IT operations, reduce infrastructure costs, and build modern private and hybrid cloud environments. ALG Data Guard is an authorized Nutanix partner providing design, deployment, VMware migration, and full managed services.
            </p>
          </div>
        </div>
      </div>

      {/* ── Hero ── */}
      <section className="nx-hero">
        <div className="nx-hero__glow-l" aria-hidden="true" />
        <div className="nx-hero__glow-r" aria-hidden="true" />
        <div className="nx-hero__grid" aria-hidden="true" />
        <div className="nx-hero__inner">
          <div>
            <div className="nx-tag nx-tag--dark"><span className="nx-dot" /> Nutanix Authorized Partner</div>
            <h2 className="nx-hero__title">
              Infrastructure That Thinks Like <em>Cloud</em>. Runs Like <em>Enterprise</em>.
            </h2>
            <p className="nx-hero__body">
              Nutanix eliminates the complexity of traditional IT infrastructure — replacing separate compute, storage, and networking silos with a single software-defined platform that scales linearly, self-manages, and spans from your data center to any public cloud. ALG Data Guard deploys and manages Nutanix for organizations building modern private and hybrid cloud environments.
            </p>
            <div className="nx-hero__actions">
              <button className="nx-btn nx-btn--purple" onClick={() => navigate("/contact")}>
                Design My Nutanix Infrastructure <ArrowIcon />
              </button>
              <button className="nx-btn nx-btn--ghost" onClick={() => navigate("/contact")}>
                VMware Migration Assessment
              </button>
            </div>
            <div className="nx-hero__stats">
              <div>
                <div className="nx-hero__stat-val">25,000+</div>
                <div className="nx-hero__stat-label">Enterprise Customers</div>
              </div>
              <div>
                <div className="nx-hero__stat-val">60%</div>
                <div className="nx-hero__stat-label">TCO Reduction vs 3-Tier</div>
              </div>
              <div>
                <div className="nx-hero__stat-val">Partner</div>
                <div className="nx-hero__stat-label">ALG Data Guard Tier</div>
              </div>
            </div>
          </div>

          {/* Cloud Layer Stack */}
          <div className="nx-cloud-stack">
            {cloudLayers.map(layer => (
              <div key={layer.label} className={`nx-cloud-layer nx-cloud-layer--${layer.cls}`}>
                <div className="nx-cloud-layer__label">{layer.label}</div>
                <div className="nx-cloud-layer__title">{layer.title}</div>
                <div className="nx-cloud-layer__nodes">
                  {layer.nodes.map(n => (
                    <span key={n.t} className={`nx-layer-node nx-layer-node--${n.c}`}>{n.t}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── HCI Platform ── */}
      <section className="nx-hci">
        <div className="nx-shell">
          <div className="nx-hci__inner">
            <div>
              <div className="nx-tag nx-tag--purple"><span className="nx-dot" /> Hyperconverged Infrastructure</div>
              <h2 className="nx-hci__title">Replace 3-Tier with One Software-Defined Platform</h2>
              <p className="nx-hci__body">Nutanix HCI consolidates compute, storage, and networking into a single platform running on standard x86 hardware — eliminating the complexity and cost of separate SAN, NAS, and blade server infrastructure. Each Nutanix node is a fully functional data center in a 2U box — and clusters scale from 3 nodes to thousands with linear, predictable performance.</p>
              <p className="nx-hci__body">ALG Data Guard designs Nutanix clusters for your specific workload mix — whether you're virtualizing VMs with AHV, running containers on NKE, hosting databases on Volumes, or building a VDI environment with Citrix or VMware Horizon on Nutanix.</p>
              <div className="nx-hci__pillars">
                {hciPillars.map(p => (
                  <div key={p.title} className="nx-hci-pillar">
                    <span className="nx-hci-pillar__icon">{p.icon}</span>
                    <div className="nx-hci-pillar__title">{p.title}</div>
                    <p className="nx-hci-pillar__desc">{p.desc}</p>
                  </div>
                ))}
              </div>
              <div style={{ marginTop:"2rem" }}>
                <button className="nx-btn nx-btn--purple" onClick={() => navigate("/contact")}>
                  Size My Nutanix Cluster <ArrowIcon />
                </button>
              </div>
            </div>
            <img
              src="https://images.unsplash.com/photo-1558494949-ef010cbdcc31?auto=format&fit=crop&w=900&q=80"
              alt="Nutanix hyperconverged infrastructure"
              className="nx-hci__img"
            />
          </div>
        </div>
      </section>

      {/* ── Platform Services ── */}
      <section className="nx-services">
        <div className="nx-shell">
          <div className="nx-services__head">
            <div className="nx-tag nx-tag--dark"><span className="nx-dot" /> Platform Services</div>
            <h2 className="nx-title">The Complete Nutanix Platform</h2>
            <p className="nx-sub" style={{ margin:"0 auto", maxWidth:"640px" }}>Nutanix's software portfolio covers every infrastructure layer — from HCI core to Kubernetes and cloud management — all unified through Prism Central.</p>
          </div>
          <div className="nx-services__grid">
            {servicesData.map(s => (
              <div key={s.title} className={`nx-service-card ${s.cls}`}>
                <span className="nx-service-card__icon">{s.icon}</span>
                <h3 className="nx-service-card__title">{s.title}</h3>
                <p className="nx-service-card__desc">{s.desc}</p>
                <div className="nx-service-card__tags">{s.tags.map(t => <span key={t} className="nx-service-tag">{t}</span>)}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Kubernetes ── */}
      <section className="nx-kubernetes">
        <div className="nx-shell">
          <div className="nx-kubernetes__inner">
            <img
              src="https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=900&q=80"
              alt="Nutanix Kubernetes platform"
              className="nx-kubernetes__img"
            />
            <div>
              <div className="nx-tag nx-tag--purple"><span className="nx-dot" /> Kubernetes Platform</div>
              <h2 className="nx-kubernetes__title">Enterprise Kubernetes, Built Into Your Cluster</h2>
              <p className="nx-kubernetes__body">Nutanix Kubernetes Engine (NKE) provides enterprise-grade Kubernetes lifecycle management directly within the Nutanix cluster — no separate Kubernetes infrastructure to maintain. NKE clusters appear alongside VMs in Prism Central, with consistent storage, networking, and security policies applied automatically.</p>
              <p className="nx-kubernetes__body">ALG Data Guard deploys NKE for organizations modernizing applications — running containerized workloads alongside traditional VMs on the same Nutanix infrastructure, simplifying operations without forklift infrastructure changes.</p>
              <div className="nx-kubernetes__list">
                {k8sItems.map(item => (
                  <div key={item.title} className="nx-k8s-item">
                    <span className="nx-k8s-item__icon">{item.icon}</span>
                    <div>
                      <div className="nx-k8s-item__title">{item.title}</div>
                      <p className="nx-k8s-item__desc">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── AI Infrastructure ── */}
      <section className="nx-ai">
        <div className="nx-shell">
          <div className="nx-ai__inner">
            <div>
              <div className="nx-tag nx-tag--dark"><span className="nx-dot" /> AI Infrastructure</div>
              <h2 className="nx-ai__title">Your AI Factory, On Your Own Infrastructure</h2>
              <p className="nx-ai__body">Nutanix GPT-in-a-Box provides a validated reference architecture for running generative AI workloads on-premises — with NVIDIA GPU nodes, Kubernetes orchestration, and persistent storage all managed through Prism Central. Keep your AI training data and model weights in your own data center with the performance of purpose-built GPU infrastructure.</p>
              <div className="nx-ai__features">
                {aiFeatures.map(f => (
                  <div key={f.name} className="nx-ai-feat">
                    <span className="nx-ai-feat__icon">{f.icon}</span>
                    <div>
                      <div className="nx-ai-feat__name">{f.name}</div>
                      <p className="nx-ai-feat__desc">{f.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ marginTop:"2rem" }}>
                <button className="nx-btn nx-btn--purple" onClick={() => navigate("/contact")}>
                  Explore GPT-in-a-Box <ArrowIcon />
                </button>
              </div>
            </div>
            <img
              src="https://images.unsplash.com/photo-1677442135703-1787eea5ce01?auto=format&fit=crop&w=900&q=80"
              alt="Nutanix AI infrastructure"
              className="nx-ai__img"
            />
          </div>
        </div>
      </section>

      {/* ── Migration ── */}
      <section className="nx-migration">
        <div className="nx-shell">
          <div className="nx-migration__head">
            <div className="nx-tag nx-tag--purple" style={{ margin:"0 auto 1rem" }}><span className="nx-dot" /> VMware Migration</div>
            <h2 className="nx-title">Migrate from VMware to Nutanix — Risk-Free</h2>
            <p className="nx-sub" style={{ margin:"0 auto", maxWidth:"640px" }}>ALG Data Guard uses Nutanix Move to migrate your VMware vSphere environment to Nutanix AHV — live, with no extended maintenance windows and no workload disruption.</p>
          </div>
          <div className="nx-migration__phases">
            {migrationPhases.map(p => (
              <div key={p.title} className="nx-migration-phase">
                <div className="nx-migration-phase__num">{p.icon}</div>
                <h3 className="nx-migration-phase__title">{p.title}</h3>
                <p className="nx-migration-phase__desc">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Industries ── */}
      <section className="nx-industries">
        <div className="nx-shell">
          <div className="nx-industries__head">
            <div className="nx-tag nx-tag--purple"><span className="nx-dot" /> Industry Solutions</div>
            <h2 className="nx-title">Nutanix Across Every Enterprise Vertical</h2>
            <p className="nx-sub" style={{ margin:"0 auto", maxWidth:"580px" }}>Nutanix delivers validated reference architectures and compliance configurations for every enterprise industry.</p>
          </div>
          <div className="nx-industries__grid">
            {industries.map(ind => (
              <div key={ind.name} className="nx-industry-card">
                <span className="nx-industry-card__icon">{ind.icon}</span>
                <h3 className="nx-industry-card__name">{ind.name}</h3>
                <p className="nx-industry-card__desc">{ind.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Metrics ── */}
      <section className="nx-metrics" ref={metricsRef}>
        <div className="nx-shell">
          <div className="nx-metrics__inner">
            <div><div className="nx-metric__val">{c1}+</div><div className="nx-metric__label">Enterprise Customers</div></div>
            <div><div className="nx-metric__val">{c2}%</div><div className="nx-metric__label">Avg TCO Reduction</div></div>
            <div><div className="nx-metric__val">{c3}.9%</div><div className="nx-metric__label">Availability SLA</div></div>
            <div><div className="nx-metric__val">{c4}%</div><div className="nx-metric__label">Faster VM Provisioning</div></div>
          </div>
        </div>
      </section>

      {/* ── Certs ── */}
      <section className="nx-certs">
        <div className="nx-shell">
          <div className="nx-certs__head">
            <div className="nx-tag nx-tag--purple"><span className="nx-dot" /> Partner Credentials</div>
            <h2 className="nx-title">Certified Nutanix Partner</h2>
            <p className="nx-sub" style={{ margin:"0 auto", maxWidth:"580px" }}>ALG Data Guard's Nutanix certifications validate our depth of expertise across the full platform portfolio.</p>
          </div>
          <div className="nx-certs__grid">
            {certs.map(c => (
              <div key={c.name} className="nx-cert-card">
                <span className="nx-cert-card__icon">{c.icon}</span>
                <div className="nx-cert-card__name">{c.name}</div>
                <p className="nx-cert-card__desc">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="nx-faq">
        <div className="nx-shell">
          <div className="nx-faq__head">
            <div className="nx-tag nx-tag--purple" style={{ margin:"0 auto 1rem" }}><span className="nx-dot" /> FAQs</div>
            <h2 className="nx-title">Nutanix Questions, Answered</h2>
            <p className="nx-sub" style={{ margin:"0 auto", maxWidth:"580px" }}>Expert technical answers about Nutanix HCI, cloud platform, and ALG Data Guard's delivery services.</p>
          </div>
          <div className="nx-faq__list">
            {faqData.map((faq, i) => (
              <div key={i} className={`nx-faq-item${openFaq === i ? " open" : ""}`}>
                <button className="nx-faq-item__trigger" onClick={() => setOpenFaq(openFaq === i ? null : i)} aria-expanded={openFaq === i}>
                  <span className="nx-faq-item__q">{faq.q}</span>
                  <span className="nx-faq-item__arrow"><ChevronDown /></span>
                </button>
                <div className="nx-faq-item__answer"><div className="nx-faq-item__answer-inner">{faq.a}</div></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="nx-cta">
        <div className="nx-cta__inner">
          <div className="nx-tag nx-tag--dark" style={{ margin:"0 auto 1.2rem" }}><span className="nx-dot" /> Get Started</div>
          <h2 className="nx-cta__title">Modernize Your Infrastructure with Nutanix</h2>
          <p className="nx-cta__sub">Whether you're building your first HCI cluster, migrating from VMware, deploying Kubernetes, or extending to hybrid cloud — ALG Data Guard's certified Nutanix engineers deliver the infrastructure your digital business demands.</p>
          <div className="nx-cta__actions">
            <button className="nx-btn nx-btn--purple" onClick={() => navigate("/contact")}>
              Request a Nutanix Assessment <ArrowIcon />
            </button>
            <button className="nx-btn nx-btn--ghost" onClick={() => navigate("/contact")}>
              VMware Migration Planning
            </button>
          </div>
        </div>
      </section>

    </div>
  );
}
